/**
 * Helpers and static preset ranges for the Nike DateRange form input
 * @fileoverview Date adjustment, range calculation, and predefined range utilities
 * @exports adjustDate, calculateInputRange, predefinedRanges, and related helpers
 * @author {Cognizant Technology Solutions}
 * @flow
 */

/**
 * Adjusts a date by a number of days and normalizes time to start or end of day.
 * @param {Date} date Base date to adjust
 * @param {number} days Number of days to add (negative values move backward)
 * @param {boolean} isEndDate When true, sets time to end of day
 * @returns {Date} Adjusted date
 */
export const adjustDate = (
  date: Date = new Date(), days: number = 0, isEndDate: boolean = false
) => {
  if (Number.isNaN(days)) {
    return date;
  }
  const adjustedDate = new Date(date);
  adjustedDate.setDate(adjustedDate.getDate() + days);
  if (isEndDate) {
    adjustedDate.setHours(23, 59, 59, 999);
  } else {
    adjustedDate.setHours(0, 0, 0, 0);
  }
  return adjustedDate;
};

/**
 * Ensures start and end dates are in chronological order for range selection.
 * @param {Object} params Selection payload with startDate and endDate
 * @param {Date} params.startDate Selected start date
 * @param {Date} params.endDate Selected end date
 * @param {string} [params.key='selection'] Selection key used by the date picker
 * @returns {Object} Ordered selection object
 */
export const getCorrectSelectionOrder = ({ startDate, endDate, key = 'selection' }: {
  startDate: Date, endDate: Date, key?: string
} = {}) => {
  if (startDate && endDate && (startDate > endDate)) {
    return {
      key,
      startDate: endDate,
      endDate: startDate
    };
  }
  return {
    key,
    startDate,
    endDate
  };
};

/**
 * Converts selected dates into day offsets relative to today for input range fields.
 * @param {Object} params Date range values from the picker
 * @param {Object} [params.startDate] Selected start date
 * @param {Object} [params.endDate] Selected end date
 * @returns {Object} Day offsets with startDays and endDays keys
 */
export const calculateInputRange = ({ startDate, endDate }: {
  startDate?: Object, endDate?: Object
}) => {
  let startDaysFromToday = null;
  let endDaysFromToday = null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (startDate && startDate.getDate && !Number.isNaN(startDate.getDate())) {
    const startDateWithoutTime = startDate;
    startDateWithoutTime.setHours(0, 0, 0, 0);
    startDaysFromToday = Math.round((startDateWithoutTime - today) / 86400000);
  }
  if (endDate && endDate.getDate && !Number.isNaN(endDate.getDate())) {
    const endDateWithoutTime = endDate;
    endDateWithoutTime.setHours(0, 0, 0, 0);
    endDaysFromToday = Math.round((endDateWithoutTime - today) / 86400000);
  }
  return {
    startDays: startDaysFromToday,
    endDays: endDaysFromToday
  };
};

/**
 * Builds start and end dates from day-offset input values.
 * @param {Object} params Input range values
 * @param {Object} [params.startFromDays] Start offset in days from today
 * @param {Object} [params.endFromDays] End offset in days from today
 * @returns {Object} Resolved startDate and endDate values
 */
export const calculateDateFromInputRange = ({
  startFromDays,
  endFromDays
}: { startFromDays?: Object, endFromDays?: Object } = {}) => {
  const today = new Date();
  let startDate = null;
  let endDate = null;
  const startDaysInNumber = Number.parseInt(String(startFromDays ?? ''), 10);
  const endDaysInNumber = Number.parseInt(String(endFromDays ?? ''), 10);
  if (!Number.isNaN(startDaysInNumber)) {
    startDate = adjustDate(today, startDaysInNumber);
  }
  if (!Number.isNaN(endDaysInNumber)) {
    endDate = adjustDate(today, endDaysInNumber);
  }
  return {
    startDate,
    endDate
  };
};

/**
 * Returns the inclusive day count between two dates.
 * @param {Object} startDate Range start date
 * @param {Object} endDate Range end date
 * @returns {number} Number of days in the range
 */
export const getDifferenceInDays = (startDate: Object, endDate: Object) => {
  const diffTime = (endDate || adjustDate(new Date(), 0, true))
    - (startDate || adjustDate(new Date(), 0));
  return Math.ceil(diffTime / 86400000);
};

/** Predefined quick-select date ranges for the DateRange picker. */
export const predefinedRanges = [
  {
    label: 'Last Month',
    range: () => {
      const today = new Date();
      const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
      return ({
        startDate: adjustDate(lastMonthStart, 0),
        endDate: adjustDate(lastMonthEnd, 0, true)
      });
    },
    id: 'LAST_MONTH',
    hasCustomRendering: true
  },
  {
    label: 'Last week',
    range: () => {
      const today = new Date();
      const thisDay = today.getDay();

      return ({
        startDate: adjustDate(today, -((thisDay === 0) ? 7 : thisDay) - 6),
        endDate: adjustDate(today, -((thisDay === 0) ? 7 : thisDay), true)
      });
    },
    id: 'LAST_WEEK',
    hasCustomRendering: true
  },
  {
    label: 'Today',
    range: () => ({
      startDate: adjustDate(new Date(), 0),
      endDate: adjustDate(new Date(), 0, true)
    }),
    hasCustomRendering: true,
    id: 'TODAY'
  },
  {
    label: 'Next week',
    range: () => {
      const today = new Date();
      const thisDay = today.getDay();
      return ({
        startDate: adjustDate(today, 8 - ((thisDay === 0) ? 7 : thisDay)),
        endDate: adjustDate(today, 14 - ((thisDay === 0) ? 7 : thisDay), true)
      });
    },
    hasCustomRendering: true,
    id: 'NEXT_WEEK'
  },

  {
    label: 'Next month',
    range: () => {
      const today = new Date();
      const nextMonthStart = new Date(today.getFullYear(), today.getMonth() + 1, 1);
      const nextMonthEnd = new Date(nextMonthStart.getFullYear(), nextMonthStart.getMonth() + 1, 0);
      return ({
        startDate: adjustDate(nextMonthStart, 0),
        endDate: adjustDate(nextMonthEnd, 0, true)
      });
    },
    hasCustomRendering: true,
    id: 'NEXT_MONTH'
  }
];

/**
 * Normalizes a date value to a display string.
 * @param {any} date Date object or string value
 * @returns {string} String representation of the date
 */
export const getDateString = (date: any) => {
  if (typeof date === 'string') {
    return date;
  }
  if (date instanceof Date) {
    return date.toDateString();
  }
  return '';
};

/**
 * Resolves a predefined range id to its start and end dates.
 * @param {string} id Predefined range identifier
 * @returns {Object} startDate and endDate for the matching preset
 */
export const getDateRangeFromStaticRange = (id: string) => {
  const currentRange = predefinedRanges
    .find((predefinedRange) => predefinedRange.id === id);
  const today = new Date();
  return (currentRange ? currentRange.range() : {
    startDate: adjustDate(today, 0),
    endDate: adjustDate(today, 0, true)
  });
};
